import { ReactElement, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { Keyboard, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import 'swiper/css';
import BannerPage from './BannerPage';
import EcologyPage from './EcologyPage';
import EconomyPage from './EconomyPage';
import React from 'react';
import ApplicationPage from './ApplicationPage';
import { useIsMobile } from '@/services';
import styles from '@/styles/pages/home/page-scroller.module.less';
import JoinUs from '@/components/join-us';

export interface PageScollerProps {
    initialPageIndex?: number;
    onChangeScroll?: (name: number, isEnd: boolean) => void;
}

const PageScoller = React.forwardRef((props: PageScollerProps, ref): ReactElement => {
    const { initialPageIndex = 0, onChangeScroll } = props;
    const [pageIndex, setPageIndex] = useState(initialPageIndex);
    const swiperRef = useRef<SwiperInstance | null>(null);

    const isMobile = useIsMobile();

    const syncPageChange = useCallback(
        (currentIndex: number) => {
            setPageIndex(currentIndex);
            onChangeScroll && onChangeScroll(currentIndex, currentIndex === 3);
        },
        [onChangeScroll],
    );

    const requestPageChange = useCallback(
        (currentIndex: number) => {
            setPageIndex(currentIndex);
            onChangeScroll && onChangeScroll(currentIndex, currentIndex === 3);
            swiperRef.current?.slideTo(currentIndex);
        },
        [onChangeScroll],
    );

    useImperativeHandle(ref, () => ({
        gotoPage(nextPageIndex: number) {
            requestPageChange(nextPageIndex);
        },
    }));

    const pages = [<BannerPage key="banner" />, <EconomyPage key="economy" />, <EcologyPage key="ecology" />, <ApplicationPage key="application" />];

    const renderPage = (page: ReactElement, index: number) => (
        <div key={page.key ?? index} className={`${styles['scene']} ${pageIndex === index ? `${styles['scene-active']} home-scene-active` : ''}`} aria-hidden={pageIndex !== index}>
            {page}
        </div>
    );

    if (isMobile) {
        return (
            <div className={styles['home-scroller']}>
                <BannerPage />
                <EconomyPage />
                <EcologyPage />
                <ApplicationPage />
            </div>
        );
    }

    return (
        <div className={styles['home-scroller']}>
            <div className={styles['desktop-shell']}>
                <Swiper
                    className={styles['desktop-swiper']}
                    direction="vertical"
                    initialSlide={initialPageIndex}
                    keyboard={{ enabled: true }}
                    modules={[Keyboard, Mousewheel]}
                    mousewheel={{ forceToAxis: true, releaseOnEdges: true, thresholdDelta: 18 }}
                    resistanceRatio={0}
                    speed={900}
                    onSlideChange={(swiper) => syncPageChange(swiper.activeIndex)}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                    }}>
                    {pages.map((page, index) => (
                        <SwiperSlide key={page.key ?? index}>{renderPage(page, index)}</SwiperSlide>
                    ))}
                </Swiper>
                <nav className={styles['page-indicator']} aria-label="Homepage sections">
                    {pages.map((page, index) => (
                        <button key={page.key ?? index} type="button" className={pageIndex === index ? styles['indicator-active'] : ''} onClick={() => requestPageChange(index)} aria-label={`Go to section ${index + 1}`} aria-current={pageIndex === index ? 'step' : undefined}>
                            <span>{String(index + 1).padStart(2, '0')}</span>
                        </button>
                    ))}
                </nav>
                <JoinUs xUrl="https://x.com/aicadegalaxy" discordUrl="https://discord.gg/J5ZjzF4C2Y" />
            </div>
        </div>
    );
});

export default PageScoller;
